import { useEffect, useState } from "react";
import { Card, CardHeader, CardBody, Header2 } from "../../../../components/ui";
import { SignupMethod } from "./SignupMethod";
import { EmailSignup } from "./EmailSignup";
import { OneClickSignup } from "./OneClickSignup";
import { WhatsAppSignup } from "./WhatsAppSignup";
import { MobileSignup } from "./MobileSignup";
import { useLocation, useNavigate } from "react-router-dom";
import { SignupMethodTypes } from "../../constants/signupMethodTypes";
import { OneClickSignupConfirmation } from "./OneClickSignupConfirmation";
import { authService } from "../../services/auth.service";
import { localStorageService } from "../../../../services";
import common from "../../../../utils/common";
import { CommonConfigsResponse } from "../../../../models/Response/commonConfigResponse";
import { LoginResponse } from "../../models/response/LoginResponse";
import { RegistrationType } from "../../../../models/constants/registrationType";
import { DeviceType } from "../../../../models/constants/deviceType";
import { SupportedLanguages } from "../../../../models/constants/supportedLanguages";
import { BannerImageResponse } from "../../models";
import userDeviceStore from "../../../../store/device.store";
import localization from "../../../../config/localization";
import { commonService } from "../../../../services/common.service";
import { BASE_URL } from "../../../../config/appConfig";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import { getTurnstileSiteKey } from "../../services/turnstile.service";

type SignupMethodType =
  | typeof SignupMethodTypes.Email
  | typeof SignupMethodTypes.Mobile
  | typeof SignupMethodTypes.OneClick
  | typeof SignupMethodTypes.WhatsApp;

export const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectUrl = (location.state as any)?.from?.pathname || "/";
  const [selectedMethod, setSelectedMethod] = useState<SignupMethodType | null>(
    null,
  );
  const [oneClickConfirmed, setOneClickConfirmed] = useState(false);
  const [userName, setUserName] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [config, setConfig] = useState<CommonConfigsResponse | null>(null);
  const [banners, setBanners] = useState<BannerImageResponse>();
  const [allowedRegistrationMethods, setAllowedRegistrationMethods] = useState<
    string[]
  >([]);
  const [registerWhatsappNumber, setRegisterWhatsappNumber] =
    useState<string>("");
  const [registerWhatsappMessage, setRegisterWhatsappMessage] =
    useState<string>("");

  const deviceType = userDeviceStore((state) => state.deviceType);

  useLanguage();

  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string>("");

  useEffect(() => {
    setTurnstileSiteKey(getTurnstileSiteKey());
  }, []);

  useEffect(() => {
    const fetchCommonConfig = async () => {
      const response = await commonService.fetchCommonConfig();
      if (response.isSuccess) {
        setConfig(response.data);
      }
    };
    fetchCommonConfig();
  }, []);

  const handleSignupMethodClick = (method: SignupMethodType) => {
    if (method === SignupMethodTypes.WhatsApp) {
      const whatsappNumber = registerWhatsappNumber || config?.whatsappNumber;
      const whatsappMessage =
        (registerWhatsappMessage || config?.whatsappMessage) ?? "";

      if (!whatsappNumber) return;

      window.open(
        `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`,
        "_blank",
      );
      navigate("/");
      return;
    } else {
      setSelectedMethod(method);
      setOneClickConfirmed(false);
    }
  };

  const fetchMasterSettingsRegisterWhatsappDetail = async () => {
    const response =
      await commonService.retrieveMasterSettingsRegisterWhatsappDetail();

    if (response.isSuccess) {
      const data = response.data?.details ?? [];

      const whatsappNumberRow = data.find(
        (x) => x.Name?.trim().toLowerCase() === "whatsappnumber",
      );
      const whatsappMessageRow = data.find(
        (x) => x.Name?.trim().toLowerCase() === "whatsappmessage",
      );

      setRegisterWhatsappNumber(whatsappNumberRow?.Value ?? "");
      setRegisterWhatsappMessage(whatsappMessageRow?.Value ?? "");

      const method = data.find(
        (x) => x.Name?.trim().toLowerCase() === "register type",
      );

      if (method?.Value) {
        const order = [
          "Email",
          "Mobile",
          "OneClick",
          "Manual/WhatsApp",
        ] as const;
        const orderIndex = (m: string) => {
          const normalized = m.trim().toLowerCase();
          if (normalized.includes("whatsapp"))
            return order.indexOf("Manual/WhatsApp");
          if (normalized === "email") return order.indexOf("Email");
          if (normalized === "mobile") return order.indexOf("Mobile");
          if (normalized.includes("oneclick")) return order.indexOf("OneClick");
          return -1;
        };

        const parsedMethods = method.Value.split(",")
          .map((x) => x.trim())
          .filter((x) => x && x !== "-");

        setAllowedRegistrationMethods(
          parsedMethods.sort((a, b) => orderIndex(a) - orderIndex(b)),
        );
        return;
      }
    }
    setAllowedRegistrationMethods([]);
  };

  useEffect(() => {
    fetchMasterSettingsRegisterWhatsappDetail();
  }, []);

  useEffect(() => {
    const fetchBanners = async () => {
      if (!deviceType) {
        return;
      }

      const response = await authService.retrieveBanners({
        bannerType:
          deviceType === DeviceType.Web ? "SignUp Desktop" : "SignUp Mobile",
        deviceType: deviceType,
        language:
          localStorageService.getItem<string>("languageCode") ||
          SupportedLanguages.EnglishLanguage,
      });

      if (response.isSuccess) {
        setBanners(response.data ?? undefined);
      }
    };

    fetchBanners();
  }, [deviceType]);

  const request = {
    registerIpAddress: config?.ip ?? "",
    browser: common.detectBrowser(),
    operatingSystem: common.detectOperatingSystem(),
    resolution: common.detectScreenResolution(),
    browserVersion: common.detectBrowserVersion(),
    device: common.detectDeviceType(),
    role: "user",
    ipAddress: config?.ip ?? "",
    loginUrl: window.location.origin,
    accessMode: userDeviceStore.getState().accessModeSource.sourceType,
    captchaSiteKey: turnstileSiteKey,
  };

  const saveUserDetails = (response: LoginResponse) => {
    localStorageService.setItem("token", response.token ?? "");
    localStorageService.setItem("refreshToken", response.refreshToken ?? "");
    localStorageService.setItem("username", response.username ?? "");
    localStorageService.setItem(
      "providerUserId",
      response.providerUserId ?? "",
    );
    localStorageService.setItem("userId", response.id ?? "");
    localStorageService.setItem("username", response.username ?? "");
    localStorageService.setItem("isUserLoggedIn", true);
  };

  const onEmailSignup = async (
    email: string,
    password: string,
    username: string,
    captchaToken?: string | null,
  ) => {
    const response = await authService.signUp({
      ...request,
      username,
      password,
      email,
      registrationType: RegistrationType.Email,
      captchaToken,
    });

    if (response.isSuccess) {
      saveUserDetails(response.data as LoginResponse);
      navigate(redirectUrl);
      window.location.reload();
    }
  };

  const onMobileSignup = async (
    username: string,
    password: string,
    mobile: string,
    captchaToken?: string | null,
  ) => {
    const response = await authService.signUp({
      ...request,
      captchaToken,
      username,
      password,
      mobile,
      registrationType: RegistrationType.Mobile,
    });

    if (response.isSuccess) {
      saveUserDetails(response.data as LoginResponse);
      navigate(redirectUrl);
      window.location.reload();
    }
  };

  const oneClickSignup = async (captchaToken: string) => {
    const response = await authService.oneClickSignup({
      ...request,
      captchaToken,
      registrationType: RegistrationType.OneClick,
    });

    if (response.isSuccess) {
      setUserName(response.data?.username ?? "");
      setUserPassword(response.data?.password ?? "");
      saveUserDetails(response.data as LoginResponse);
      setOneClickConfirmed(true);
    }
  };

  const onChangeMethod = () => {
    setSelectedMethod(null);
    setOneClickConfirmed(false);
  };

  const navigateToLogin = () => navigate("/login");

  const handleNavigateBanner = (bannerLink?: string | null) => {
    if (!bannerLink || bannerLink === "undefined") {
      return;
    }

    if (bannerLink.includes(BASE_URL)) {
      const redirectedUrl = bannerLink.replace(
        BASE_URL,
        window.location.origin,
      );
      window.location.href = redirectedUrl;
      return;
    }

    if (bannerLink.startsWith("http://") || bannerLink.startsWith("https://")) {
      window.location.href = bannerLink;
      return;
    }

    navigate(bannerLink);
  };

  let content;
  if (!selectedMethod) {
    content = (
      <SignupMethod
        handleSignupMethodClick={handleSignupMethodClick}
        allowedRegistrationMethods={allowedRegistrationMethods}
      />
    );
  } else if (selectedMethod === SignupMethodTypes.Email) {
    content = (
      <EmailSignup
        turnstileSiteKey={turnstileSiteKey}
        onChangeMethod={onChangeMethod}
        onSubmit={onEmailSignup} />
    );
  } else if (selectedMethod === SignupMethodTypes.Mobile) {
    content = (
      <MobileSignup
        turnstileSiteKey={turnstileSiteKey}
        onChangeMethod={onChangeMethod}
        onSubmit={onMobileSignup} />
    );
  } else if (selectedMethod === SignupMethodTypes.OneClick) {
    content = oneClickConfirmed ? (
      <OneClickSignupConfirmation
        userName={userName}
        password={userPassword}
        redirectUrl={redirectUrl}
      />
    ) : (
      <OneClickSignup
        turnstileSiteKey={turnstileSiteKey}
        onChangeMethod={onChangeMethod}
        onContinue={oneClickSignup}
      />
    );
  } else if (selectedMethod === SignupMethodTypes.WhatsApp) {
    content = <WhatsAppSignup />;
  }

  const shouldShowBanner =
    deviceType === DeviceType.Web &&
    banners?.details &&
    banners.details.length > 0;

  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    const el = document.getElementById("signup-content");
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      setContentHeight(entries[0].contentRect.height);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Card>
      {deviceType === DeviceType.Mobile && (
        <CardHeader showBackButton={true} showLogo={true} />
      )}
      <CardBody showGradient={true}>
        <div
          className={
            shouldShowBanner
              ? "grid grid-cols-1 md:grid-cols-2 items-start gap-10"
              : "flex justify-center items-center min-h-[500px]"
          }
        >
          {shouldShowBanner && (
            <div className="hidden md:block overflow-hidden">
              <img
                src={`data:image/png;base64,${banners?.details?.[0]?.banner}`}
                alt="Banner"
                className="w-full h-full object-cover rounded-xl cursor-pointer"
                style={{ height: contentHeight }}
                onClick={() =>
                  handleNavigateBanner(banners?.details?.[0]?.bannerLink)
                }
              />
            </div>
          )}

          <div
            id="signup-content"
            className={shouldShowBanner ? "pt-5" : "w-full max-w-xl"}
          >
            <Header2 className="text-white mb-6">
              {localization.create_an_account}
            </Header2>

            {content}

            <div
              className="text-center text-white text-m mt-5 py-4 cursor-pointer"
              onClick={navigateToLogin}
            >
              {localization.already_have_an_account}{" "}
              <span className="font-bold">{localization.login}</span>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};
